import type { FlowGraph, ModuleCatalog } from './types';
import { http } from '../api/http';

// Read + write API against Aivrmanager.Api. The "no-claim, no-draft" policy is
// implemented on the server: every write hits the active flow row directly,
// so any other session looking at the same flow will pick the change up on
// next refresh. Commit/discard are still TODOs (we may not need them at all
// for this customer set), so they remain rejected stubs that surface a clear
// error message instead of silently no-op'ing.

class FeatureNotPortedError extends Error {
  constructor(action: string) {
    super(
      `'${action}' is not available in this version of the Flow Builder.`,
    );
    this.name = 'FeatureNotPortedError';
  }
}

function notPorted<T>(action: string): Promise<T> {
  return Promise.reject<T>(new FeatureNotPortedError(action));
}

export function fetchModuleCatalog(): Promise<ModuleCatalog> {
  return http<ModuleCatalog>('/api/flowbuilder/module-catalog');
}

export function fetchFlowGraph(applicationId: number, flowId: number): Promise<FlowGraph> {
  return http<FlowGraph>(`/api/applications/${applicationId}/flows/${flowId}/graph`);
}

export function saveFlowLayout(applicationId: number, flowId: number, layoutJson: string) {
  return http<{ result: boolean }>(
    `/api/applications/${applicationId}/flows/${flowId}/layout`,
    {
      method: 'POST',
      body: JSON.stringify({ layoutJson }),
    },
  );
}

// -- Writes -----------------------------------------------------------------
// Minimal payload shapes intentionally — we don't pass customerId because
// the API derives it from the authenticated session.

export function addNode(
  applicationId: number,
  flowId: number,
  moduleId: number,
  nodeName: string,
): Promise<{ result: boolean; id: number }> {
  return http<{ result: boolean; id: number }>(
    `/api/applications/${applicationId}/flows/${flowId}/nodes`,
    {
      method: 'POST',
      body: JSON.stringify({ moduleId, nodeName }),
    },
  );
}

export function deleteNode(
  applicationId: number,
  flowId: number,
  nodeId: number,
): Promise<{ result: boolean }> {
  return http<{ result: boolean }>(
    `/api/applications/${applicationId}/flows/${flowId}/nodes/${nodeId}`,
    {
      method: 'DELETE',
    },
  );
}

export function setNodeExit(
  applicationId: number,
  flowId: number,
  nodeId: number,
  exitId: number,
  referenceNodeId: number | null,
): Promise<{ result: boolean }> {
  return http<{ result: boolean }>(
    `/api/applications/${applicationId}/flows/${flowId}/nodes/${nodeId}/exits/${exitId}`,
    {
      method: 'PUT',
      body: JSON.stringify({ referenceNodeId }),
    },
  );
}

export function setNodeExitActive(
  applicationId: number,
  flowId: number,
  nodeId: number,
  exitId: number,
  isActive: boolean,
): Promise<{ result: boolean }> {
  return http<{ result: boolean }>(
    `/api/applications/${applicationId}/flows/${flowId}/nodes/${nodeId}/exits/${exitId}/active`,
    {
      method: 'PATCH',
      body: JSON.stringify({ isActive }),
    },
  );
}

export function updateNode(
  applicationId: number,
  flowId: number,
  nodeId: number,
  nodeName: string,
  params: Array<{ id: number; typeName: string; value: string }>,
): Promise<{ result: boolean }> {
  return http<{ result: boolean }>(
    `/api/applications/${applicationId}/flows/${flowId}/nodes/${nodeId}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        nodeName,
        parameters: params,
      }),
    },
  );
}

export function updateFlow(payload: {
  customerId: number;
  applicationId: number;
  flowId: number;
  flowName: string;
  initialNodeId: number | null;
}): Promise<{ result: boolean }> {
  return http<{ result: boolean }>(
    `/api/applications/${payload.applicationId}/flows/${payload.flowId}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        flowName: payload.flowName,
        initialNodeId: payload.initialNodeId,
      }),
    },
  );
}

/**
 * Creates a new application-scoped variable. Used by the "&lt;New variable&gt;"
 * option in every variable picker — the SPA passes only a name, the API
 * defaults to ApplicationVariableTypeId = 1 (Application scope), matching
 * the legacy designer's default.
 *
 * Throws a `FetchError` with status 409 when the name already exists, so
 * the modal can show a friendly message instead of a generic 500.
 */
export function createVariable(
  applicationId: number,
  name: string,
): Promise<{ id: number; name: string }> {
  return http<{ id: number; name: string }>(
    `/api/applications/${applicationId}/variables`,
    {
      method: 'POST',
      body: JSON.stringify({ name }),
    },
  );
}

// Commit/discard are not part of this no-draft policy; left as loud stubs so
// any UI that surfaces them doesn't silently succeed when nothing happened.

export function commitFlow(
  _applicationId: number,
  _flowId: number,
  _purgeUnused: boolean,
): Promise<{ result: boolean }> {
  return notPorted('Flow committen');
}

export function discardFlow(
  _applicationId: number,
  _flowId: number,
): Promise<{ result: boolean }> {
  return notPorted('Flow verwerpen');
}
